import React, { useCallback, useMemo, useState, useEffect, useRef } from "react"
import PropTypes from "prop-types"
import { ReactComponent as CheckIcon } from "../../../../../assets/icons/check-icon.svg"
import FormSectionAccordion from "../../../../../components/FormSectionAccordion"
import GridFormSheet from "../../../../../components/GridFormSheet"
import ReactInputMask from "react-input-mask"
import TextField from "@mui/material/TextField"
import {removeTableHeader} from "src/v2/utils/table-clipboard"

const DateInlineCell = ({ params, inlineHandlers, onUserEdit }) => {
    const value = params?.value || ""
    const [localValue, setLocalValue] = useState(value)

    useEffect(() => {
        setLocalValue(value)
    }, [value])

    const handleChange = (event) => {
        setLocalValue(event.target.value)
    }

    const handleBlur = (event) => {
        const newValue = event.target.value

        // Marca a linha como editada pelo usuário
        onUserEdit?.(params.id)

        inlineHandlers.onChangeValue(params.id, params.field, newValue)
        inlineHandlers.onBlurPersist(params.id, params.field, newValue, params.row)
    }

    const handleKeyDown = (event) => {
        const keysToStop = ["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"]
        if (keysToStop.includes(event.key)) event.stopPropagation()
        if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "a") {
            event.stopPropagation()
        }
    }

    return (
        <ReactInputMask
            mask="99/99/9999"
            value={localValue}
            onChange={handleChange}
            onBlur={handleBlur}
            maskChar=""
        >
            {(inputProps) => (
                <TextField
                    {...inputProps}
                    variant="standard"
                    fullWidth
                    autoFocus={false}
                    onKeyDown={handleKeyDown}
                    InputProps={{ disableUnderline: true, sx: { fontSize: "14px" } }}
                />
            )}
        </ReactInputMask>
    )
}

DateInlineCell.propTypes = {
    params: PropTypes.object.isRequired,
    inlineHandlers: PropTypes.shape({
        onChangeValue: PropTypes.func.isRequired,
        onBlurPersist: PropTypes.func.isRequired,
    }).isRequired,
    onUserEdit: PropTypes.func,
}

const CovenantsSection = ({
    actions,
    onRegisterAutofill,
    defaultOpen = false,
    rows,
    onChangeRows,
    syncToken = 0,
    onPersistRow,
    onDeleteRow,
    onRowUpdateError,
    onRowsPasted,
    emissao,
    vencimento,
    isValid,
    showError,
}) => {
    const userEditedRowsRef = useRef(new Set())

    const markRowAsEdited = useCallback((rowId) => {
        userEditedRowsRef.current.add(rowId)
    }, [])

    const DateMaskCell = useCallback(
        (params, inlineHandlers) => (
            <DateInlineCell
                params={params}
                inlineHandlers={inlineHandlers}
                onUserEdit={markRowAsEdited}
            />
        ),
        [markRowAsEdited]
    )

    const effectiveRows = rows || []

    const columnsOrder = useMemo(
        () => [
            "dataValidadeInicial",
            "dataValidadeFinal",
            "tipoCovenant",
            "clausula",
            "numerador",
            "denominador",
            "limite",
            "observacao",
        ],
        []
    )

    const columnsConfig = useMemo(
        () => [
            {field: "dataValidadeInicial", headerName: "Data validade inicial", renderInlineCell: DateMaskCell},
            {field: "dataValidadeFinal", headerName: "Data validade final", renderInlineCell: DateMaskCell},
            {field: "tipoCovenant", headerName: "Tipo covenant"},
            {field: "clausula", headerName: "Cláusula"},
            {field: "numerador", headerName: "Numerador"},
            {field: "denominador", headerName: "Denominador"},
            {field: "limite", headerName: "Limite"},
            {field: "observacao", headerName: "Observação"},
        ],
        [DateMaskCell]
    )

    const transformPasteCells = useCallback(
        (cells, {isTablePaste}) => isTablePaste
            ? removeTableHeader(cells, columnsOrder, columnsConfig.map(column => column.headerName))
            : cells,
        [columnsOrder, columnsConfig]
    )
    // Atualiza datas quando emissão muda (somente linhas não editadas)
    useEffect(() => {
        if (!emissao) return
        onChangeRows((prev) =>
            (prev || []).map((row) => {
                if (userEditedRowsRef.current.has(row.id)) return row
                return {
                    ...row,
                    dataValidadeInicial: emissao,
                }
            })
        )
    }, [emissao, onChangeRows])

    // Atualiza datas quando vencimento muda (somente linhas não editadas)
    useEffect(() => {
        if (!vencimento) return
        onChangeRows((prev) =>
            (prev || []).map((row) => {
                if (userEditedRowsRef.current.has(row.id)) return row
                return {
                    ...row,
                    dataValidadeFinal: vencimento,
                }
            })
        )
    }, [vencimento, onChangeRows])

    const initializeEmptyRow = useCallback(row => {
        if (row.__initialized || row.dataValidadeInicial || row.dataValidadeFinal || row.tipoCovenant) return row
        return {
            ...row,
            dataValidadeInicial: emissao || "",
            dataValidadeFinal: vencimento || "",
            tipoCovenant: "financeiro",
            __initialized: true,
        }
    }, [emissao, vencimento])

    // Inicializa linha nova vazia criada pelo Grid.
    useEffect(() => {
        if (!effectiveRows.some(row => !row.__initialized && !row.dataValidadeInicial && !row.dataValidadeFinal && !row.tipoCovenant)) return
        onChangeRows(prev => prev.map(initializeEmptyRow))
    }, [effectiveRows, initializeEmptyRow, onChangeRows])

    const handleRowsPasted = useCallback(pastedRows => {
        const initializedRows = pastedRows.map(initializeEmptyRow)
        if (initializedRows.some((row, index) => row !== pastedRows[index])) {
            onChangeRows(prev => prev.map(row => initializedRows.find(pasted => pasted.id === row.id) || row))
        }
        onRowsPasted?.(initializedRows)
    }, [initializeEmptyRow, onChangeRows, onRowsPasted])
    // Autofill
    const handleAutofillInternal = useCallback(
        (rowGenerator) => {
            const generated = rowGenerator ? rowGenerator() : {}
            onChangeRows((prev) => [
                ...(prev || []),
                {
                    ...generated,
                    id: generated.id || `${Date.now()}-${Math.random()}`,
                    dataValidadeInicial: emissao || "",
                    dataValidadeFinal: vencimento || "",
                    tipoCovenant: generated.tipoCovenant || "financeiro",
                },
            ])
        },
        [onChangeRows, emissao, vencimento]
    )

    useEffect(() => {
        if (!onRegisterAutofill) return
        const api = {
            autofill: handleAutofillInternal,
            clear: () => onChangeRows([]),
        }
        onRegisterAutofill(api)
        return () => onRegisterAutofill(undefined)
    }, [handleAutofillInternal, onChangeRows, onRegisterAutofill])

    return (
        <FormSectionAccordion
            title="Covenants"
            icon={<CheckIcon style={{ color: "#F2B947", width: 20, height: 20 }} />}
            isValid={isValid} 
            showError={showError}
            actions={actions}
            defaultOpen={defaultOpen}
            syncToken={syncToken}
        >
            <GridFormSheet
                rows={effectiveRows}
                onChange={onChangeRows}
                columnsOrder={columnsOrder}
                columnsConfig={columnsConfig}
                onPersistRow={onPersistRow}
                onDeleteRow={onDeleteRow}
                onRowUpdateError={onRowUpdateError}
                alwaysEditable
                addRowPosition="start"
                pasteMode="append"
                onRowsPasted={handleRowsPasted}
                transformPasteCells={transformPasteCells}
            />
        </FormSectionAccordion>
    )
}

CovenantsSection.propTypes = {
    actions: PropTypes.node,
    onRegisterAutofill: PropTypes.func,
    defaultOpen: PropTypes.bool,
    rows: PropTypes.array,
    onChangeRows: PropTypes.func.isRequired,
    syncToken: PropTypes.number,
    onPersistRow: PropTypes.func,
    onDeleteRow: PropTypes.func,
    onRowUpdateError: PropTypes.func,
    onRowsPasted: PropTypes.func,
    emissao: PropTypes.string,
    vencimento: PropTypes.string,
    isValid: PropTypes.bool, 
    showError: PropTypes.bool,
}

export default CovenantsSection
